import React from "react";
import { Activity, HandHelping, Check, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const ServiceCard = ({ data, icon: Icon, idAnchor, testId }) => (
  <article
    id={idAnchor}
    className="group relative rounded-3xl bg-white border border-slate-100 shadow-sm p-7 md:p-8 hover:shadow-lg hover:border-green-200 transition-all duration-300 scroll-mt-24"
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-green-50 text-green-700 group-hover:bg-green-100 transition-colors">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900">
        {data.name}
      </h3>
    </div>
    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{data.desc}</p>

    <div className="mt-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-green-700">
        {data.techTitle}
      </p>
      <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-2">
        {data.techniques.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
            <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-green-100 text-green-700">
              <Check className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
            {it}
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-6 pt-6 border-t border-slate-100">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
        {data.condTitle}
      </p>
      <ul className="mt-3 flex flex-wrap gap-1.5">
        {data.conditions.map((c, i) => (
          <li
            key={i}
            className="inline-flex rounded-full bg-slate-50 border border-slate-100 px-2.5 py-1 text-xs text-slate-700"
          >
            {c}
          </li>
        ))}
      </ul>
    </div>
  </article>
);

export const FisioQuiro = () => {
  const { t } = useLang();
  const s = t.services;
  return (
    <section
      id="services"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      data-testid="services-section"
    >
      <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-green-100/40 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-100 px-3 py-1 text-xs font-semibold tracking-widest uppercase text-green-700">
            <Activity className="h-3.5 w-3.5" /> {s.eyebrow}
          </span>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            {s.title}
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">{s.desc}</p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6 items-start">
          <ServiceCard
            data={s.fisica}
            icon={Activity}
            idAnchor="services-fisica"
            testId="services-card-fisica"
          />
          <ServiceCard
            data={s.quiro}
            icon={HandHelping}
            idAnchor="services-quiro"
            testId="services-card-quiro"
          />
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-3xl bg-gradient-to-br from-green-600 to-emerald-700 p-7 md:p-8 text-white shadow-xl shadow-green-900/10 flex flex-col justify-center">
            <h3 className="font-display text-2xl font-bold tracking-tight">
              {s.cta}
            </h3>
            <p className="mt-2 text-sm text-green-50/90 max-w-md">
              {s.desc}
            </p>
            <a
              href="#booking-fisio"
              className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-white text-green-700 px-6 py-3.5 text-sm font-semibold hover:bg-green-50 transition-colors shadow-md"
              data-testid="services-cta"
            >
              {s.cta}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-7">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-green-700">
              <Sparkles className="h-3 w-3" /> {s.benefitsTitle}
            </div>
            <ul className="mt-5 space-y-3">
              {s.benefits.map((b, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-green-600 text-white">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FisioQuiro;
